import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import api, { apiErr } from "../lib/api";
import { fmtMoney, fmtSigned, pnlColor } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Play, Loader2, ShieldAlert, FileDown } from "lucide-react";
import { jsPDF } from "jspdf";

const PERIOD_LABEL = { "0.5": "6M", "1": "1Y", "2": "2Y", "3": "3Y" };

function Stat({ label, value, cls = "" }) {
  return (
    <div className="border border-[#1E2028] bg-[#05050A] p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono">{label}</div>
      <div className={`mt-1 text-lg font-mono tabular ${cls}`}>{value}</div>
    </div>
  );
}

export default function BacktestPanel() {
  const { t } = useLang();
  const [opts, setOpts] = useState(null);
  const [symbol, setSymbol] = useState("XAU/USD");
  const [strategy, setStrategy] = useState("Momentum");
  const [timeframe, setTimeframe] = useState("1h");
  const [years, setYears] = useState("1");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  useEffect(() => { api.get("/backtest/options").then((r) => setOpts(r.data)).catch(() => {}); }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/backtest", { symbol, strategy, timeframe, years: Number(years) });
      setRes(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const exportPdf = () => {
    if (!res) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    let y = 48;
    doc.setFillColor(5, 5, 10); doc.rect(0, 0, W, 90, "F");
    doc.setFillColor(250, 204, 21); doc.rect(40, 30, 30, 30, "F");
    doc.setTextColor(5, 5, 10); doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.text("A", 50, 52);
    doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.text("AURORA TRADER AI", 82, 48);
    doc.setTextColor(250, 204, 21); doc.setFontSize(9); doc.text("BACKTEST REPORT", 82, 64);
    y = 120;
    doc.setTextColor(30, 30, 30); doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text(`${res.symbol}  ·  ${res.strategy}  ·  ${res.timeframe}`, 40, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(110, 110, 110);
    doc.text(`${res.bars_tested} bougies testées  ·  généré le ${new Date().toLocaleString()}`, 40, y + 16);

    const rows = [
      ["Rendement", `${res.return_pct}%`], ["Capital final", `€${res.end_equity}`],
      ["Win Rate", `${res.win_rate}%`], ["Profit Factor", `${res.profit_factor}`],
      ["Max Drawdown", `${res.max_drawdown}%`], ["Sharpe", `${res.sharpe}`],
      ["Sortino", `${res.sortino}`], ["Nombre de trades", `${res.num_trades}`],
      ["Gain moyen", `€${res.avg_win}`], ["Perte moyenne", `€${res.avg_loss}`],
      ["Frais", `€${res.fees}`], ["Slippage", `€${res.slippage}`],
    ];
    y += 40;
    const colW = (W - 80) / 2;
    doc.setFontSize(10);
    rows.forEach((r, i) => {
      const col = i % 2, row = Math.floor(i / 2);
      const x = 40 + col * colW, ry = y + row * 26;
      doc.setDrawColor(225, 225, 225); doc.line(x, ry + 6, x + colW - 12, ry + 6);
      doc.setTextColor(120, 120, 120); doc.setFont("helvetica", "normal"); doc.text(r[0], x, ry);
      doc.setTextColor(20, 20, 20); doc.setFont("helvetica", "bold"); doc.text(r[1], x + colW - 12, ry, { align: "right" });
    });

    // equity curve plot
    let cy = y + Math.ceil(rows.length / 2) * 26 + 30;
    const cx0 = 40, cw = W - 80, ch = 180;
    doc.setTextColor(20, 20, 20); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Courbe d'équité", cx0, cy); cy += 12;
    doc.setDrawColor(220, 220, 220); doc.rect(cx0, cy, cw, ch);
    const eq = res.equity_curve.map((p) => p.equity);
    const mn = Math.min(...eq), mx = Math.max(...eq), rng = mx - mn || 1;
    doc.setDrawColor(250, 204, 21); doc.setLineWidth(1.2);
    for (let i = 1; i < eq.length; i++) {
      const x1 = cx0 + (i - 1) / (eq.length - 1) * cw;
      const x2 = cx0 + i / (eq.length - 1) * cw;
      const y1 = cy + ch - (eq[i - 1] - mn) / rng * ch;
      const y2 = cy + ch - (eq[i] - mn) / rng * ch;
      doc.line(x1, y1, x2, y2);
    }
    doc.setLineWidth(1); doc.setTextColor(150, 150, 150); doc.setFontSize(8);
    doc.text(`€${mx.toFixed(0)}`, cx0 + 2, cy + 10);
    doc.text(`€${mn.toFixed(0)}`, cx0 + 2, cy + ch - 4);

    cy += ch + 26;
    doc.setTextColor(180, 120, 0); doc.setFontSize(8); doc.setFont("helvetica", "italic");
    doc.text("Resultats simules — non representatifs des performances futures. Aucun rendement n'est garanti.", 40, cy, { maxWidth: W - 80 });

    doc.save(`AURORA_backtest_${res.symbol.replace("/", "-")}_${res.strategy.replace(/ /g, "-")}_${res.timeframe}.pdf`);
    toast.success("PDF exporté");
  };

  const Ctrl = ({ label, value, onChange, items, render }) => (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#6B7280] font-mono mb-1">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-[#05050A] border-[#1E2028] text-sm h-9 font-mono" data-testid={`bt-${label}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#0F1015] border-[#1E2028] text-white">
          {items.map((it) => (
            <SelectItem key={String(it)} value={String(it)} className="text-sm focus:bg-[#17181F]">
              {render ? render(it) : it}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="border border-[#1E2028] bg-[#0F1015] p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <Ctrl label={t("instrument")} value={symbol} onChange={setSymbol} items={opts?.symbols || [symbol]} />
          <Ctrl label={t("strategy")} value={strategy} onChange={setStrategy} items={opts?.strategies || [strategy]} />
          <Ctrl label={t("chart")} value={timeframe} onChange={setTimeframe} items={opts?.timeframes || [timeframe]} />
          <Ctrl label={t("period")} value={years} onChange={setYears} items={(opts?.periods || [1]).map(String)}
            render={(v) => PERIOD_LABEL[v] || `${v}Y`} />
          <button data-testid="run-backtest" onClick={run} disabled={busy}
            className="bg-[#FACC15] text-[#05050A] font-semibold h-9 text-sm hover:bg-[#eab308] transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} {t("runBacktest")}
          </button>
        </div>
        <div className="mt-3 flex items-center gap-1.5 text-[10px] text-[#F59E0B] font-mono">
          <ShieldAlert className="w-3 h-3" /> {t("btDisclaimer")}
        </div>
      </div>

      {busy && <div className="text-center text-sm text-[#6B7280] font-mono py-8">{t("running")}</div>}

      {res && !busy && (
        <div className="space-y-4 fade-up" data-testid="backtest-results">
          <div className="flex justify-end">
            <button data-testid="export-pdf" onClick={exportPdf}
              className="flex items-center gap-2 border border-[#FACC15] text-[#FACC15] text-xs font-semibold px-3 py-2 hover:bg-[#FACC15]/10 transition-colors duration-200">
              <FileDown className="w-3.5 h-3.5" /> {t("exportPdf")}
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Stat label={t("returnPct")} value={`${res.return_pct >= 0 ? "+" : ""}${res.return_pct}%`} cls={pnlColor(res.return_pct)} />
            <Stat label={t("winRate")} value={`${res.win_rate}%`} cls="text-[#FACC15]" />
            <Stat label={t("profitFactor")} value={res.profit_factor} cls={res.profit_factor >= 1 ? "text-[#10B981]" : "text-[#EF4444]"} />
            <Stat label={t("maxDrawdown")} value={`${res.max_drawdown}%`} cls="text-[#EF4444]" />
            <Stat label="Sharpe" value={res.sharpe} />
            <Stat label="Sortino" value={res.sortino} />
            <Stat label={t("numTrades")} value={res.num_trades} />
            <Stat label={t("avgWin")} value={fmtSigned(res.avg_win)} cls="text-[#10B981]" />
            <Stat label={t("avgLoss")} value={fmtSigned(res.avg_loss)} cls="text-[#EF4444]" />
            <Stat label={t("endEquity")} value={fmtMoney(res.end_equity)} />
            <Stat label={t("fees")} value={fmtMoney(res.fees)} cls="text-[#9CA3AF]" />
            <Stat label={t("slippage")} value={fmtMoney(res.slippage)} cls="text-[#9CA3AF]" />
          </div>

          <div className="border border-[#1E2028] bg-[#0F1015] p-4">
            <div className="text-xs uppercase tracking-wider text-[#9CA3AF] font-mono mb-3">
              {t("capitalCurve")} · {res.symbol} · {res.strategy} · {res.timeframe} · {res.bars_tested} {t("barsTested").toLowerCase()}
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={res.equity_curve}>
                <defs>
                  <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FACC15" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#FACC15" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1E2028" vertical={false} />
                <XAxis dataKey="i" stroke="#6B7280" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} />
                <YAxis stroke="#6B7280" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} domain={["auto", "auto"]} />
                <Tooltip contentStyle={{ background: "#05050A", border: "1px solid #1E2028", fontFamily: "IBM Plex Mono", fontSize: 12 }}
                  labelStyle={{ color: "#6B7280" }} formatter={(v) => [fmtMoney(v), "Equity"]} />
                <Area type="monotone" dataKey="equity" stroke="#FACC15" strokeWidth={2} fill="url(#eq)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
